
import { useState, useEffect, useCallback, useRef } from "react";

interface SearchFilterOptions {
  initialQuery?: string;
  onQueryChange?: (query: string) => void;
  debounceMs?: number;
}

export function useSearchFilter({
  initialQuery = "",
  onQueryChange,
  debounceMs = 100 // Further reduced from 150ms to 100ms for even faster response
}: SearchFilterOptions = {}) {
  const [searchQuery, setSearchQuery] = useState(initialQuery);
  const [debouncedQuery, setDebouncedQuery] = useState(initialQuery);
  const isMounted = useRef(true);

  // Set up the mounted ref
  useEffect(() => {
    isMounted.current = true;
    console.log(`SearchFilter initialized with: "${initialQuery}"`);
    return () => {
      isMounted.current = false;
    };
  }, [initialQuery]);

  // Debounce the search query
  useEffect(() => {
    const timer = setTimeout(() => {
      if (isMounted.current) {
        console.log(`Debounced search query: "${searchQuery}"`);
        setDebouncedQuery(searchQuery);
      }
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [searchQuery, debounceMs]);

  // Notify parent of query changes
  useEffect(() => {
    if (onQueryChange && isMounted.current) {
      onQueryChange(debouncedQuery);
    }
  }, [debouncedQuery, onQueryChange]);

  // Handle search
  const handleSearch = useCallback((query: string) => {
    console.log(`Setting search query: "${query}"`);
    setSearchQuery(query);
  }, []);

  // Clear search
  const clearSearch = useCallback(() => {
    console.log("Clearing search");
    setSearchQuery("");
    setDebouncedQuery("");
    
    if (onQueryChange) {
      onQueryChange("");
    }
  }, [onQueryChange]);

  return {
    searchQuery,
    debouncedQuery,
    setSearchQuery,
    handleSearch,
    clearSearch
  };
}
